import {
  EffectConfig,
  EffectParams,
  EffectPreset,
  EffectRangeAll,
  GetPresetFunction,
  OneOfEffects,
  OneOfScrollEffects,
  SchemaNumber,
  SchemaString,
  ScrollEffectConfig,
  ScrollEffectParams,
} from '../types';
import * as allEffects from './allEffects';

export {
  isTriggerAllowed,
  getAllowedTriggers,
  isAnimatable,
} from './componentWhitelist';

export { fixEffect } from './effectsFixer';
export type { EffectToFix, EffectFixer } from './effectsFixer';

export function getDefaults(
  schema: EffectPreset['schema'],
  type: 'mobileDefault' | 'previewDefault' | 'default' = 'default',
) {
  const defaults = Object.fromEntries(
    Object.entries(schema).map(([name, params]) => {
      const value = params[type] ?? params.default;
      return [
        name,
        value !== undefined
          ? value
          : (params as SchemaString).enum
          ? (params as SchemaString).enum?.[0]
          : (params as SchemaNumber).min,
      ];
    }),
  ) as EffectParams | ScrollEffectParams;
  return defaults;
}

export function isLegacy(preset: EffectPreset) {
  return preset.groups?.includes('legacy');
}

export const getPresets: GetPresetFunction = (
  { config, includeGroups, excludeGroups, rangeGroup, names } = {
    config: 'wix',
  },
  isDeveloperMode = false,
) =>
  Object.entries(allEffects)
    // Get config for the correct editor
    .map(([, effect]) =>
      config === 'wix'
        ? effect.config
        : config === 'responsive'
        ? effect.responsiveConfig
        : ({} as EffectConfig<OneOfEffects>),
    )
    // Cleanup any unsupported data, for example jest __esModule stuff
    .filter((v) => v)
    // Include groups
    .filter((effectConfig) => isDeveloperMode || !effectConfig.hide)
    .filter((effectConfig) =>
      includeGroups
        ? includeGroups.every(
            (group) =>
              effectConfig.groups.includes(group) ||
              (
                effectConfig as ScrollEffectConfig<OneOfScrollEffects>
              ).range?.enum?.includes(group as EffectRangeAll),
          )
        : false,
    )
    // Exclude groups
    .filter((effectConfig) =>
      excludeGroups
        ? !excludeGroups.some(
            (group) =>
              effectConfig.groups.includes(group) ||
              (
                effectConfig as ScrollEffectConfig<OneOfScrollEffects>
              ).range?.enum?.includes(group as EffectRangeAll),
          )
        : true,
    )
    // Include just animations with specified name
    .filter((effectConfig) =>
      names ? names.includes(effectConfig.name) : true,
    )
    .map((effectConfig) => {
      // if config includes a rangeGroup, return only the relevant schema
      if (!rangeGroup) {
        return effectConfig;
      }
      const { schema, ...effect } =
        effectConfig as ScrollEffectConfig<OneOfScrollEffects>;
      return { ...effect, rangeGroup, schema: { ...schema[rangeGroup] } };
    });
